import { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, FileText, CheckSquare, Zap, Lightbulb } from 'lucide-react';
import { useConversation } from '../hooks/useConversation';

interface AssistantChatPanelProps {
  emailId: string | null;
  userName: string;
}

const SUGGESTED_ACTIONS = [
  {
    icon: Zap,
    label: 'Create a new category',
    prompt: 'Help me create a new email category to organize my inbox better.'
  },
  {
    icon: FileText,
    label: 'Find important emails I missed',
    prompt: 'Can you help me find important emails that I might have missed or overlooked?'
  },
  {
    icon: CheckSquare,
    label: 'Tell me about my writing style',
    prompt: 'Based on the emails I\'ve received, what can you tell me about my writing style and how I communicate?'
  },
  {
    icon: Sparkles,
    label: 'Don\'t summarize this style',
    prompt: 'I\'d prefer if you didn\'t use summary formats for my emails. Can you adjust how you present information?'
  },
];

export default function AssistantChatPanel({
  emailId,
  userName,
}: AssistantChatPanelProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [localMessages, setLocalMessages] = useState<Array<{ id: string; role: 'user' | 'assistant'; content: string }>>([]);

  const {
    messages: apiMessages,
    isLoading,
    isSending,
    error,
    sendMessage,
  } = useConversation(emailId);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [apiMessages, localMessages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isSending) return;

    const message = input.trim();
    setInput('');
    
    // Add user message to local state immediately
    const userMsg = {
      id: `local-${Date.now()}`,
      role: 'user' as const,
      content: message,
    };
    setLocalMessages(prev => [...prev, userMsg]);

    // If we have an email, send through the API
    if (emailId) {
      await sendMessage(message);
    } else {
      // Otherwise, simulate a response
      setTimeout(() => {
        const assistantMsg = {
          id: `local-${Date.now() + 1}`,
          role: 'assistant' as const,
          content: 'Select an email to get more context-specific responses from Claude.',
        };
        setLocalMessages(prev => [...prev, assistantMsg]);
      }, 500);
    }
  };

  const handleSuggestedAction = (prompt: string) => {
    if (isSending) return;
    
    // Add to local messages
    const userMsg = {
      id: `local-${Date.now()}`,
      role: 'user' as const,
      content: prompt,
    };
    setLocalMessages(prev => [...prev, userMsg]);
    
    // If we have an email, send through API
    if (emailId) {
      sendMessage(prompt);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Use local messages if no email is selected, otherwise use API messages
  const messages = emailId ? apiMessages : localMessages;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Claude Assistant</h3>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Hello {userName}.
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          </div>
        ) : messages.length === 0 ? (
          <div className="space-y-4">
            {/* Greeting Section */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-100 dark:border-blue-800/50">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                ✨ Quick Actions
              </h4>
              <div className="space-y-2">
                {SUGGESTED_ACTIONS.map(({ icon: Icon, label, prompt }) => (
                  <button
                    key={label}
                    onClick={() => handleSuggestedAction(prompt)}
                    disabled={isSending}
                    className="w-full flex items-center gap-3 p-3 text-left rounded-lg border border-blue-200 dark:border-blue-700/50 bg-white dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    <Icon className="w-4 h-4 text-blue-500 shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Pro Tip Box */}
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-200 text-sm mb-1">
                    Pro Tip
                  </p>
                  <p className="text-amber-800 dark:text-amber-300 text-xs leading-relaxed">
                    Select an email to ask Claude specific questions about it, get summaries, extract action items, or draft replies.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-blue-500 text-white rounded-br-none'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-none'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                </div>
              </div>
            ))}

            {/* Thinking indicator */}
            {isSending && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-none px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything..."
              rows={1}
              className="input resize-none pr-4 min-h-[42px] max-h-32 text-sm"
              style={{ height: 'auto' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 128) + 'px';
              }}
            />
          </div>
          <button
            type="submit"
            disabled={!input.trim() || isSending}
            className="p-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Send message"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>

        {/* Quick actions after conversation starts */}
        {messages.length > 0 && (
          <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
            {SUGGESTED_ACTIONS.slice(0, 2).map(({ label, prompt }) => (
              <button
                key={label}
                onClick={() => handleSuggestedAction(prompt)}
                disabled={isSending}
                className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {label.split(' ').slice(0, 2).join(' ')}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
